import { useEffect, useState } from "react";
import { Shuffle, Trash2, Sparkles, Trophy, Award, Check, X, GitBranch } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { api, errMsg } from "../../lib/api";
import { Empty, Loader } from "../common";

const teamName = (b, id, fallback) => (id === "BYE" ? "BYE (folga)" : id ? b.teams[id]?.label || "Dupla removida" : fallback);

const Options = ({ data, onPick, busy }) => (
  <div>
    <div className="mb-4 flex items-center gap-2 text-sm text-[#94A3B8]"><Sparkles className="h-4 w-4 text-[#E4F222]" />
      <span><b className="text-white">{data.teams} duplas</b> inscritas. Sugestões ordenadas da melhor para a menos indicada:</span></div>
    <div className="grid gap-3 md:grid-cols-2">
      {data.options.map((o) => (
        <div key={o.key} data-testid={`format-option-${o.key}`} className={`card-x p-5 ${o.recommended ? "border-[#E4F222] lime-glow" : ""}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="h-display text-2xl">{o.title}</div>
            {o.recommended && <span className="rounded-full bg-[#E4F222] px-2.5 py-0.5 text-[10px] font-bold uppercase text-black">Recomendado</span>}
          </div>
          <p className="mt-1 text-sm text-[#94A3B8]">{o.description}</p>
          <div className="mt-3 flex gap-4 text-xs text-[#94A3B8]">
            <span><b className="text-white">{o.matches}</b> jogos</span>
            <span>mín. <b className="text-white">{o.min_games}</b> jogos por dupla</span>
          </div>
          <button disabled={busy} data-testid={`generate-${o.key}`} onClick={() => onPick(o)} className={`mt-4 w-full rounded-full py-2.5 text-sm ${o.recommended ? "btn-lime" : "btn-ghost"}`}>
            <Shuffle className="mr-1 inline h-4 w-4" />Sortear chaves neste formato
          </button>
        </div>
      ))}
    </div>
  </div>
);

const MatchCard = ({ b, m, isAdmin, onSave }) => {
  const [edit, setEdit] = useState(false);
  const [sa, setSa] = useState(m.score_a ?? "");
  const [sb, setSb] = useState(m.score_b ?? "");
  useEffect(() => { setSa(m.score_a ?? ""); setSb(m.score_b ?? ""); }, [m.score_a, m.score_b]);
  const ready = m.team_a && m.team_b && !m.is_bye;
  const row = (side, id, label, score) => {
    const won = m.winner_team && m.winner_team === id && id !== "BYE";
    return (
      <div className={`flex items-center justify-between gap-2 px-3 py-2 ${won ? "text-[#E4F222]" : ""}`}>
        <span className={`truncate text-sm ${id ? "font-semibold" : "italic text-[#64748B]"}`}>{teamName(b, id, label)}</span>
        {edit ? (
          <input data-testid={`score-${side}-${m.number}`} type="number" min="0" className="inp h-8 w-14 px-2 text-center" value={side === "a" ? sa : sb}
            onChange={(e) => (side === "a" ? setSa(e.target.value) : setSb(e.target.value))} />
        ) : <span className="font-display text-xl font-bold">{score ?? "-"}</span>}
      </div>
    );
  };
  const save = async (clear) => {
    await onSave(m.id, clear ? { score_a: null, score_b: null } : { score_a: Number(sa), score_b: Number(sb) });
    setEdit(false);
  };
  return (
    <div data-testid={`match-${m.number}`} className={`card-x overflow-hidden ${m.is_bye ? "opacity-50" : ""}`}>
      <div className="flex items-center justify-between border-b border-[#232B3E] bg-[#0B0E14]/60 px-3 py-1.5 text-[11px] text-[#64748B]">
        <span>Jogo {m.number}</span>
        {isAdmin && ready && !edit && <button data-testid={`edit-score-${m.number}`} onClick={() => setEdit(true)} className="font-semibold text-[#E4F222]">{m.winner ? "Editar" : "Lançar placar"}</button>}
        {edit && (
          <span className="flex gap-2">
            <button data-testid={`save-score-${m.number}`} onClick={() => save(false)} className="text-[#E4F222]"><Check className="h-4 w-4" /></button>
            {m.winner && <button data-testid={`clear-score-${m.number}`} onClick={() => save(true)} className="text-red-400"><Trash2 className="h-4 w-4" /></button>}
            <button onClick={() => setEdit(false)}><X className="h-4 w-4" /></button>
          </span>
        )}
      </div>
      {row("a", m.team_a, m.label_a, m.score_a)}
      <div className="h-px bg-[#232B3E]" />
      {row("b", m.team_b, m.label_b, m.score_b)}
    </div>
  );
};

const Standings = ({ b, name, rows }) => (
  <div className="card-x overflow-hidden">
    <div className="h-display bg-[#0B0E14]/60 px-4 py-2 text-lg text-[#E4F222]">{b.groups.length > 1 ? `Grupo ${name}` : "Classificação"}</div>
    <table className="w-full text-sm">
      <thead className="text-[11px] uppercase text-[#64748B]"><tr><th className="px-3 py-1.5 text-left">#</th><th className="text-left">Dupla</th><th>J</th><th>V</th><th>Saldo</th></tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.team} className="border-t border-[#232B3E]">
            <td className="px-3 py-2 font-display font-bold text-[#E4F222]">{i + 1}</td>
            <td className="max-w-[180px] truncate">{teamName(b, r.team)}</td>
            <td className="text-center">{r.played}</td><td className="text-center font-bold">{r.wins}</td><td className="text-center">{r.balance}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PODIUM = ["Campeão", "Vice-campeão", "3º lugar", "4º lugar"];

export default function BracketTab({ stage, level }) {
  const { isAdmin } = useAuth();
  const [b, setB] = useState(undefined);
  const [sugg, setSugg] = useState(null);
  const [showOpts, setShowOpts] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    const [br, sg] = await Promise.all([api.get(`/stages/${stage.id}/brackets`, { params: { level } }), api.get(`/stages/${stage.id}/brackets/suggest`, { params: { level } })]);
    setB(br.data[0] || null);
    setSugg(sg.data);
    setShowOpts(false);
  };
  useEffect(() => { setB(undefined); load(); }, [stage.id, level]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async (o) => {
    if (b && !window.confirm("Isso substituirá a chave atual e todos os resultados. Continuar?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/stages/${stage.id}/brackets`, { level, option: o });
      setB(data); setShowOpts(false);
      toast.success("Sorteio realizado!");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const saveScore = async (mid, body) => {
    try { const { data } = await api.put(`/brackets/${b.id}/matches/${mid}`, body); setB(data); toast.success("Placar salvo"); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm("Excluir esta chave?")) return;
    await api.delete(`/brackets/${b.id}`); toast.success("Chave excluída"); load();
  };
  const award = async () => {
    if (!window.confirm("Lançar pontos do pódio + participação para as demais duplas? (substitui lançamentos anteriores desta chave)")) return;
    try { const { data } = await api.post(`/brackets/${b.id}/award`, { participation: true }); toast.success(`${data.created} lançamentos de pontos criados`); } catch (e) { toast.error(errMsg(e)); }
  };

  if (b === undefined || !sugg) return <Loader />;
  if (!b || showOpts) {
    if (!isAdmin) return <Empty icon={GitBranch} title="Chaves não sorteadas" text="A organização ainda não realizou o sorteio das chaves deste nível." />;
    if (sugg.teams < 2) return <Empty icon={GitBranch} title="Duplas insuficientes" text="Inscreva ao menos 2 duplas para gerar as chaves." />;
    return (
      <div>
        {b && <button onClick={() => setShowOpts(false)} className="btn-ghost mb-4" data-testid="cancel-new-draw">Voltar para a chave atual</button>}
        <Options data={sugg} onPick={generate} busy={busy} />
      </div>
    );
  }
  const phases = [];
  b.matches.forEach((m) => {
    let p = phases.find((x) => x.name === m.phase);
    if (!p) phases.push((p = { name: m.phase, matches: [] }));
    p.matches.push(m);
  });
  const outdated = sugg.teams !== b.team_ids.length;
  return (
    <div className="space-y-6">
      <div className="card-x flex flex-wrap items-center justify-between gap-3 p-5">
        <div>
          <div className="eyebrow">Formato</div>
          <div data-testid="bracket-title" className="h-display text-3xl">{b.title}</div>
          <p className="text-sm text-[#94A3B8]">{b.description}</p>
          {outdated && <p className="mt-1 text-xs text-yellow-300">Atenção: a quantidade de duplas mudou desde o sorteio ({b.team_ids.length} → {sugg.teams}).</p>}
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <button data-testid="award-points-btn" onClick={award} className="btn-lime flex items-center gap-1 rounded-full px-4 py-2 text-sm"><Award className="h-4 w-4" />Lançar pontos</button>
            <button data-testid="new-draw-btn" onClick={() => setShowOpts(true)} className="btn-ghost flex items-center gap-1"><Shuffle className="h-4 w-4" />Novo sorteio</button>
            <button data-testid="delete-bracket-btn" onClick={remove} className="btn-ghost text-red-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        )}
      </div>

      {b.podium?.some(Boolean) && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="bracket-podium">
          {b.podium.map((t, i) => t && (
            <div key={i} className={`card-x p-4 ${i === 0 ? "border-[#E4F222] lime-glow" : ""}`}>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase text-[#E4F222]"><Trophy className="h-4 w-4" />{PODIUM[i]}</div>
              <div className="mt-1 text-sm font-semibold">{teamName(b, t)}</div>
            </div>
          ))}
        </div>
      )}

      {Object.keys(b.standings || {}).length > 0 && (
        <div className="grid gap-4 md:grid-cols-2">{Object.entries(b.standings).map(([n, rows]) => <Standings key={n} b={b} name={n} rows={rows} />)}</div>
      )}

      {phases.map((p) => (
        <div key={p.name}>
          <div className="h-display mb-3 text-2xl">{p.name}</div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {p.matches.map((m) => <MatchCard key={m.id} b={b} m={m} isAdmin={isAdmin} onSave={saveScore} />)}
          </div>
        </div>
      ))}
    </div>
  );
}
